// ============================================================================
// utils_deckReorder.ts — règles de réordonnancement du deck
//
// La partie qui décide, séparée de celle qui affiche : ces fonctions sont pures
// et testables sans monter la régie. C'est là que vit la seule subtilité du
// glisser-déposer des médias — deux tableaux à bouger, et pas toujours les deux.
// ============================================================================

import { MediaAssignment, MediaKind } from './types_deck';
import { swapAt } from './utils_dragReorder';

export interface MediaReorderPlan {
  /** Nouvelles affectations bouton → slot. Même référence si rien ne change. */
  assignments: (MediaAssignment | null)[];
  /**
   * Échange à répercuter dans la bibliothèque (imageSlots, soundSlots,
   * jingleSlots) — c'est-à-dire dans ce que montre la Configuration du
   * Mediaboard. `null` quand il n'y a rien à y faire.
   */
  librarySwap: { kind: MediaKind; fromId: string; toId: string } | null;
}

/**
 * Décide ce qu'il advient quand on lâche le bouton média `from` sur le `to`.
 *
 * Deux tableaux, et c'est le cœur du sujet :
 *
 * · `mediaAssignments` dit QUEL slot occupe QUEL bouton — il le désigne par son
 *   id. C'est lui qui décide de la position des boutons du deck.
 * · les bibliothèques (`imageSlots`…) sont ce que liste la Configuration du
 *   Mediaboard. Leur ordre n'entre PAS dans le calcul des positions.
 *
 * D'où la règle : les affectations s'échangent toujours (le deck bouge), la
 * bibliothèque seulement quand les deux boutons sont de la même famille. Une
 * image lâchée sur un son échange bien les deux boutons, mais il n'existe aucun
 * tableau commun où inscrire cet ordre — et il n'y en a pas besoin.
 *
 * Cette asymétrie est aussi ce qui empêche la boucle : puisque l'ordre de la
 * bibliothèque n'influe pas sur les positions, la répercuter ne peut pas
 * relancer un déplacement de boutons.
 */
export const planMediaReorder = (
  assignments: (MediaAssignment | null)[],
  from: number,
  to: number,
): MediaReorderPlan => {
  const next = swapAt(assignments, from, to);
  if (next === assignments) return { assignments, librarySwap: null };

  const a = assignments[from] ?? null;
  const b = assignments[to]   ?? null;

  const librarySwap = (a && b && a.kind === b.kind)
    ? { kind: a.kind, fromId: a.slotId, toId: b.slotId }
    : null;

  return { assignments: next, librarySwap };
};

/**
 * Applique un échange de bibliothèque désigné par ids.
 *
 * Par id et non par index : entre le moment où le bouton a été saisi et celui où
 * on écrit, la bibliothèque a pu changer (un média téléversé, un slot vidé). Un
 * index périmé échangerait deux entrées au hasard ; un id introuvable ne fait
 * simplement rien.
 */
export const applyLibrarySwap = <T extends { id: string }>(
  library: T[],
  fromId: string,
  toId: string,
): T[] => {
  const i = library.findIndex(s => s.id === fromId);
  const j = library.findIndex(s => s.id === toId);
  if (i < 0 || j < 0) return library;
  return swapAt(library, i, j);
};
